import { OnInit, Component, ViewChild, TemplateRef, ElementRef, Input, OnDestroy } from "@angular/core";
import { select } from "@angular-redux/store";
import { Observable, Subject } from "rxjs";
import { TransportOrder } from "../../../../../pb-screen/models/transport-order/transport-order.interface";
import { ToScreenService } from "../../../../../pb-screen/services/to-screen.service";
import {
  SelectableSettings,
  GridDataResult,
  DataStateChangeEvent,
  ColumnReorderEvent,
  ColumnResizeArgs
} from "@progress/kendo-angular-grid";
import { State, SortDescriptor, orderBy } from "@progress/kendo-data-query";
import { ApplicationUser, Column } from "../../../../../pb-screen/models/application.user.interface";
import { TranslateService } from "@ngx-translate/core";
import { DefaultOrderLineColumnDefiniton } from "../../../../../pb-screen/static/columns/OrderLineColumnDefinition";
import { ToActions } from "../../../../to.actions";
import { SharedActions } from "../../../../../shared/shared.actions";
import { OrderLine } from "../../../../../pb-screen/models/order-line/orderLine.interface";
import { ApplicationUserActions } from "../../../../../pb-screen/application.user.actions";
import { ToActionBarService } from "../../../../../pb-screen/services/to-action-bar.service";

import { PlanningBlock } from "../../../../../pb-screen/models/planning-block/planning-block.interface";
import { PbActions } from "../../../../../pb-screen/pb.actions";
import { takeUntil } from "rxjs/operators";
import { UserFeedbackService } from "../../../../../services/user-feedback.service";
import { ConfirmDialogService } from "../../../../../shared/components/confirm-dialog/confirm-dialog.service";
import { PlanningBoardActions } from "../../../../../planningBoard/planningBoard.action";
import { LINE_CLOSED, LINE_IN_PROGRESS } from "../../../../../pb-screen/models/status-type.interface";

@Component({
  moduleId: module.id,
  selector: "to-orderlines-table",
  templateUrl: "./to-orderlines.component.html",
})

export class ToOrderLinesComponent implements OnInit, OnDestroy {
   
  @select("applicationUser") public applicationUser$: Observable<ApplicationUser>;
  @select("sessionOrderLineColumnSelection") public sessionOrderLineColumnSelection$: Observable<Column[]>;
  @select("selectedOrderlines") public selectedOrderlines$: Observable<OrderLine[]>;
  @select("loading") public loading$: Observable<boolean>;
  @select("orderLines") public orderLines$: Observable<OrderLine[]>;

  public selectedOrderlines: OrderLine[] = [];
  public transportOrder: TransportOrder;
  public orderLines: OrderLine[] = [];
  public tableData: GridDataResult;
  public rows = [];
  public columns = DefaultOrderLineColumnDefiniton;
  public state: State = {
    skip: 0,
    take: 50,
  };
  public selectableSettings: SelectableSettings = {
    checkboxOnly: false,
    mode: "multiple",
  };
  public tableWidth: number;
  public tableHeight: number = 350;

  private tooltipOffset = { left: 0, top: 0 };
  private tooltipVisible = false;
  private tooltipValue = "";
  public user: ApplicationUser;
  private _resetSelectedRows: boolean;
  private mySelection: OrderLine[] = [];
  public planningBlocksForTo: PlanningBlock[] = [];
  windowHeight: number;
  public selectedKeys: string[] = [];

  get resetSelectedRows(): boolean {
    return this._resetSelectedRows;
  }

  @Input("resetSelectedRows")
  set resetSelectedRows(value: boolean) {
    this._resetSelectedRows = value;
  }

  public pageSize = 10;
  public skip = 0;

  public loadingSpinner = false;

  destroy$ = new Subject();

  @ViewChild("dateTemplate") public dateTemplate: TemplateRef<any>;
  @ViewChild("tableParent") tableParentOrderLines: ElementRef;
  

  constructor(
    private readonly toScreenService: ToScreenService,
    private readonly translate: TranslateService,
    private readonly toActions: ToActions,
    private readonly sharedAction: SharedActions,
    private readonly userActions: ApplicationUserActions,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly toActionBarService: ToActionBarService,
    private readonly pbActions: PbActions,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly toService: ToScreenService,
    private readonly plannBoardActions: PlanningBoardActions) {

  }

  ngOnInit(): void {
    this.loading$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (!data) {
        this.loadingSpinner = data;
      }
    });
    this.applicationUser$.pipe(takeUntil(this.destroy$)).subscribe((applicationUser: ApplicationUser) => {
      this.user = applicationUser;
    });

    this.sessionOrderLineColumnSelection$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.columns = this.createDataTablesColumns(data);
    });

    this.loadingSpinner = true;
    if(this.transportOrder) {
      this.fetchTransportOrderLines(this.transportOrder);
    }

    this.orderLines$.subscribe((orderLines: OrderLine[]) => {
      if (orderLines && orderLines.length > 0 && this.transportOrder) {

        this.orderLines = orderLines.filter((x) => x && x.transportOrderId== this.transportOrder.id);
        this.setGridContent();
      } else {
        this.orderLines = [];
        this.tableData = { data: [], total: 0 };
      }
    });

  }

  public fetchTransportOrderLines(transportOrder: TransportOrder): void {
    this.transportOrder = transportOrder;
    this.toService.getTransportOrderDetails(this.transportOrder.id).subscribe((orderlinesData: any[]) => {
      this.orderLines = orderlinesData;
      this.loadingSpinner = false;
      this.setGridContent();

    }, (error) => {
      this.userFeedbackService.showErrorMessage("SHARED.ERROR_GETTING_ORDERLINES", { error });
    });
  }

  
  ngOnDestroy(): void {
    this.resetSelection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setGridContent(): void {
    this.tableData = {
      data: [...this.orderLines.slice(this.state.skip, this.state.skip + this.state.take)],
      total: this.orderLines.length,
    };

  }

  public createDataTablesColumns(selectedColumns: Column[]): string[] {
    const result = [];
    for (const column of selectedColumns) {

      this.translate.get(column.name).subscribe((translated) => {

        result.push({
          cellTemplate: this.dateTemplate,
          name: translated,
          prop: column.name.substring(column.name.lastIndexOf(".") + 1),
          width: column.width,
        });
      });
    }
    return result;
  }

  public refreshTableDisplay(): void {
    this.state.skip = 0;
    this.setGridContent();
  }

  public moveElementInArray(array, reorderedColumnIndex, differencePos) {
    const value = array[reorderedColumnIndex];
    const arrayClone = array.slice();
    const newpos = reorderedColumnIndex + -(differencePos);
    arrayClone.splice(reorderedColumnIndex, 1);
    arrayClone.splice(newpos, 0, value);

    return arrayClone;
  }

  public showTooltip(event: any) {
    this.tooltipOffset = { left: event.pageX + 4, top: event.pageY + 4 };
    this.tooltipValue = event.srcElement.innerText;
    if (this.tooltipValue && this.tooltipValue.length > 0 &&
      (event.srcElement.localName === "td" || event.srcElement.localName === "a")
      && (event.srcElement.offsetWidth < event.srcElement.scrollWidth)) {
      this.tooltipVisible = true;
    }
  }

  public hideTooltip() {
    this.tooltipVisible = false;
  }

  public pageChange(event: DataStateChangeEvent): void {
    this.state.skip = event.skip;

    this.setGridContent();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.state.sort = sort;

    this.orderLines = orderBy([...this.orderLines], sort);

    this.setGridContent();
  }

  public reorderGridUser(event: ColumnReorderEvent) {
    const column: any = event.column;
    const reorderedColumnIndex = this.user.orderlinePreferences.OrderLineColumnSelection
      .indexOf(this.user.orderlinePreferences.OrderLineColumnSelection
        .filter((x) => x && x.name.substring(x.name.lastIndexOf(".") + 1) === column.field)[0]);

    if (reorderedColumnIndex !== -1) {
      const differencePos = event.oldIndex - event.newIndex;
      if (differencePos) {
        const updatedArray =
          this.moveElementInArray(this.user.orderlinePreferences.OrderLineColumnSelection, reorderedColumnIndex, differencePos);
        this.user.orderlinePreferences.OrderLineColumnSelection = updatedArray;
        this.userActions.updateApplicationUser(this.user, "defaultOrderLineUserSettings", true);
      }
    } else {
      this.userFeedbackService.showWarningMessage("TOASTR_MESSAGES.WARNING.COLUMN_NOT_FOUND_REORDER_FAILED");
    }
  }

  public resizeGrid(event: ColumnResizeArgs[]) {
    if (this.isTheResizedColumnValid(event)) {
      const column: any = event[0].column;
      const resizedColumnIndex = this.user.orderlinePreferences.OrderLineColumnSelection
        .indexOf(this.user.orderlinePreferences.OrderLineColumnSelection
          .filter((x) => x && x.name.substring(x.name.lastIndexOf(".") + 1) === column.field)[0]);

      if (resizedColumnIndex !== -1) {
        if (this.isNewWidthValid(event[0].newWidth)) {
          this.user.orderlinePreferences.OrderLineColumnSelection[resizedColumnIndex].width = event[0].newWidth;
          this.userActions.updateApplicationUser(this.user, "defaultOrderLineUserSettings", true);
        } else {
          this.userFeedbackService.showWarningMessage("TOASTR_MESSAGES.WARNING.COLUMN_INVALID_WIDTH_RESIZE_FAILED");
        }
      } else {
        this.userFeedbackService.showWarningMessage("TOASTR_MESSAGES.WARNING.COLUMN_NOT_FOUND_RESIZE_FAILED");
      }
    }
  }

  private isTheResizedColumnValid(event: ColumnResizeArgs[]): boolean {
    return event.length === 1 && event[0].hasOwnProperty("column");
  }

  private isNewWidthValid(newWidth: number): boolean {
    return newWidth > 0;
  }

  public rowClicked( selection): void {
    if (selection.selectedRows && selection.selectedRows.length > 0) {
      this.mySelection = [...this.mySelection.concat(selection.selectedRows.map((x) => x.dataItem))];
    }

    if (selection.deselectedRows && selection.deselectedRows.length > 0) {
      const deselectedOrderlines = selection.deselectedRows.map((x) => x.dataItem);
      this.mySelection = [...this.mySelection.filter((x) => deselectedOrderlines.indexOf(x) === -1)];
    }

    this.selectedOrderlines = this.mySelection;
  }


  /**
   * changeOrderlineSequence
   $event , sequence    */
  public changeOrderlineSequence($event, targetSequenceNo): void {

    this.loadingSpinner = true;
    if (this.mySelection.length > 0) {

      this.toScreenService.changePlanningSequenceBatch(
        this.transportOrder.id,
        targetSequenceNo,
        this.mySelection
      ).subscribe(_ => {
          this.userFeedbackService.showInfoMessage("SHARED.OPERATION_SUCCESS");

          if (this.transportOrder) {
            this.toScreenService.getTransportOrderDetails(this.transportOrder.id).subscribe((orderlines: any[]) => {
              this.loadingSpinner = false;
              this.sharedAction.getOrderlines(orderlines);
            } , (error) => {
              this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.ERROR_GETTING_ORDERLINES", { error });
              this.loadingSpinner = false;
            });
          }
          this.resetSelection();
        }, (error) => {
          this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.ERROR_CHANGE_ORDERLINE_SEQUENCE", { error });
          this.loadingSpinner = false;
        });
    }

  }

  public CanMoveRow(orderLine: OrderLine) {
    if (this.mySelection.length === 0) {
      return false;
    }
    return !this.mySelection.some(selected => selected.orderLineId === orderLine.orderLineId);
  }

  resetSelection() {
    this.selectedKeys = [];
    this.mySelection = [];
    this.selectedOrderlines = [];
    this.toActions.setSelectedOrderlines([]);
    this.toActionBarService.resetActionBar();
  }

  noPlanningRequired(item) {

    if (!this.transportOrder && !this.transportOrder.id) {
      this.userFeedbackService.showErrorMessage("SHARED.ERROR_NO_TRANSPORT_ORDER_SELECTED");
      return;
    }

    this.translate.get("SHARED.NO_PLANNING_REQUIRED_CONFIRMATION").subscribe(res => {
      this.confirmDialogService.confirmThis(res, () => {
        this.loadingSpinner = true;
        this.toScreenService.NoPlanningRequired(this.transportOrder, [item]).subscribe((data) => {
          if (data == "") {
            this.userFeedbackService.showSuccessMessage("SHARED.NO_PLANNING_REQUIRED_SUCCESS");
            this.toService.getTransportOrdersByIds(this.transportOrder.id).subscribe((transportOrders: TransportOrder[]) => {
              if (transportOrders.length > 0) {
                this.sharedAction.getOrderlines(transportOrders[0].planningOrderlines);
                this.toActions.updateTransportOrdersDisplay(transportOrders);
                this.plannBoardActions.UpdatePlanningDisplay(transportOrders, true);
                transportOrders.forEach(element => {
                  element.planningBlocks.forEach(pb => {
                    this.pbActions.updatePlanningBlocksDisplay([pb]);
                  });
                });


                this.loadingSpinner = false;
              }
            });
          } else {
            this.userFeedbackService.showErrorMessage("SHARED.ERROR_REMOVE_LINES", { error: data });
            this.loadingSpinner = false;
          }

        });
      }, () => { });
    }, () => { });
  }

  public setGridDimensions(isCollapsed: boolean = false): void {
    if (isCollapsed) {
      this.windowHeight = window.screen.height;
      this.tableHeight = this.windowHeight >= 1080 ? this.windowHeight * 0.55: 500;
    }
    else
      this.tableHeight = 350;
  }
  public isPlanningRequiredDisabled(item: OrderLine) {
    return item.lineStatus.statusType == LINE_CLOSED || item.lineStatus.statusType ==  LINE_IN_PROGRESS;
  }

}
